import { Sub, RefLink, P, Pill, ApiBlock, FlowSteps } from "./GuideHelpers";

export const SectionDataSourcesMetrics = () => (
  <>
    <Sub id="ds-landscape-metrics">3b. Landscape-Computed Metrics — Relevance &amp; KPS</Sub>
    <P>
      When the landscape is produced, two important metrics are calculated and added to each keyword:
    </P>

    <div className="rounded-lg border border-border/60 bg-surface-tint-soft p-4 mb-4 space-y-4">
      <div>
        <div className="font-semibold text-foreground text-sm mb-1">Relevance Score</div>
        <P>
          Relevance measures how closely a keyword phrase matches the meaning and context of the hero ASIN's
          listing content (title, bullets, brand name). It uses <strong>semantic similarity</strong> rather
          than simple word matching.
        </P>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-2">
          <li>
            <strong className="text-foreground">Model:</strong> Google Vertex AI <code className="text-xs bg-muted px-1 py-0.5 rounded">text-embedding-005</code> with
            768-dimension embeddings for high accuracy.
          </li>
          <li>
            <strong className="text-foreground">Inputs:</strong> Hero ASIN text (title + brand + bullet points combined)
            vs. each keyword phrase. Brand name is included by default but can be excluded.
          </li>
          <li>
            <strong className="text-foreground">Method:</strong> Cosine similarity between the hero text vector and each
            keyword vector produces a score from 0 (unrelated) to 100 (perfectly related), then scaled to a max
            of 30 points within the KPS formula.
          </li>
        </ul>
        <div className="text-xs text-muted-foreground italic mb-1">Postprocessing penalties:</div>
        <ul className="list-disc pl-5 space-y-1 text-xs text-muted-foreground">
          <li><strong className="text-foreground">Competitor Brand Penalty (×0.5)</strong> — If the keyword contains a competitor's brand name.</li>
          <li><strong className="text-foreground">No Lexical Overlap Penalty (×0.75)</strong> — If the keyword shares zero words with the hero ASIN's title, bullets, or brand.</li>
        </ul>
      </div>

      <div className="border-t border-border/40 pt-4">
        <div className="font-semibold text-foreground text-sm mb-1">Keyword Pursuit Score (KPS)</div>
        <P>
          KPS is Signal's composite score for prioritizing keyword opportunities. It factors in six dimensions:
        </P>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Search Volume</strong> — Higher search volume increases the score.
          </li>
          <li>
            <strong className="text-foreground">Competitors</strong> — The sweet spot is 1,000–10,000 competitors.
            Below that range the keyword is likely branded or too niche; above it the keyword is likely too generic
            rather than a specific, long-tail term.
          </li>
          <li>
            <strong className="text-foreground">Current Rank</strong> — Keywords where the hero ASIN is not yet #1
            but has room to close the gap are scored higher than keywords already at #1.
          </li>
          <li>
            <strong className="text-foreground">Keyword Relevance</strong> — The relevance score described above
            (max 30 points in the KPS formula).
          </li>
          <li>
            <strong className="text-foreground">Search Trend</strong> — Whether search demand for the keyword is
            increasing or decreasing over time.
          </li>
          <li>
            <strong className="text-foreground">PPC Bid Data</strong> — How expensive it would be to target the keyword
            with advertising, serving as a proxy for commercial intent.
          </li>
        </ul>
      </div>
    </div>

    <Sub id="ds-seo-insights">4. SEO Insights — AI Recommendation Engine</Sub>
    <P>
      Every week, a new SEO Insights report is generated for each product — <strong>but only if a new
      landscape was generated within the last week</strong>. If no new landscape data exists, the
      recommendations remain unchanged because there are no new insights to surface.
    </P>
    <P>
      The AI recommendations are generated by an LLM using a specialized prompt. The AI acts as an
      Amazon SEO expert analyzing all available keyword landscapes for a hero product, identifying
      the 5 best keyword opportunities to improve the product's SEO positioning.
    </P>
    <Sub>AI Recommendation Inputs</Sub>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li>All keyword landscape data available for the product</li>
      <li>Current title and bullet points</li>
      <li>Existing keyword buckets (Top Priority, Hidden Gems, Trending)</li>
    </ul>
    <Sub>AI Evaluation Criteria</Sub>
    <P>
      For every keyword considered, the AI evaluates:
    </P>
    <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li>How relevant is it to the hero product?</li>
      <li>Is the product underindexed or losing share to competitors on this term?</li>
      <li>Does this keyword show stronger opportunity than the language currently occupying the title or bullets?</li>
      <li>If inserted into the listing, would it improve positioning, search intent coverage, or conversion relevance?</li>
      <li>Would replacing existing wording create a net SEO gain, or are we better off keeping the current phrase?</li>
    </ol>
    <P>
      The AI favors keywords that represent a <strong>true trade-up</strong> versus current listing
      language — not just keywords that look good in isolation. It does not simply choose keywords
      because they have high KPS, and it avoids prioritizing keywords already in the title or bullets
      unless they are clearly among the strongest overall opportunities.
    </P>
    <Sub>AI Output Format</Sub>
    <P>
      The AI returns exactly 5 recommendations, each consisting of a <strong>keyword phrase</strong> and
      a <strong>one-sentence reason</strong> (e.g., "High volume, low competition gap vs. competitors"
      or "Trending +42% MoM; strong PPC opportunity").
    </P>

    <Sub id="ds-ab-tests">5. A/B Tests &amp; Brand Signals — Experiment Lifecycle</Sub>
    <P>
      A/B Tests are the connective tissue of Signal. Recommendations come from many surfaces
      (<RefLink to="seo-insights">SEO Insights</RefLink>,{" "}
      <RefLink to="keyword-bank">Keywords</RefLink>,{" "}
      <RefLink to="keyword-landscapes">Landscapes</RefLink>,{" "}
      <RefLink to="content-scorecard-tab">Content Scorecard</RefLink>,{" "}
      <RefLink to="brand-signals">Brand Signals</RefLink>) but all of them funnel into the
      same creation flow at <code className="text-[11px] bg-surface-tint-medium px-1.5 py-0.5 rounded">/ab-tests/create</code>.
      Every completed test then funnels back out into the brand's{" "}
      <RefLink to="brand-signals">signal pool</RefLink>, which is how Signal turns one-off
      experiments into durable, brand-level knowledge.
    </P>

    <P>
      <strong>Entry points carry context.</strong> The launch state includes the hero ASIN
      and a typed pre-fill payload — <code>prefillFromScorecard</code> for a COSMO gap,{" "}
      <code>prefillFromSignal</code> for a Brand Signal hypothesis matrix,{" "}
      <code>prefillFromKeyword</code> for a keyword launched from the Keyword Bank, SEO
      Insights, or a Landscape table, or <code>prefillFromDraft</code> when resuming a
      Drafting test. A <code>returnTo</code> path is stashed so the user lands back where
      they came from after creating, saving as draft, or cancelling. The context is also
      mirrored to sessionStorage to survive React strict-mode double-mounts and
      back/forward cache restores.
    </P>

    <Sub>Source attribution</Sub>
    <P>
      Each test records the surface it was launched from. This is what makes the loop
      visible: a Brand Signal can point back to the tests that built it, and a Content
      Scorecard gap can show which tests it spawned.
    </P>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li><code>scorecard</code> — launched from a Content Scorecard gap card.</li>
      <li><code>brand-signal</code> — launched from the Brand Signals drawer.</li>
      <li><code>keyword</code> / <code>landscape</code> / <code>seo-insight</code> — launched from a per-row or bulk affordance on the Keyword Bank, a unified Landscape table, or SEO Insights. Seeds the target phrase + a templated hypothesis.</li>
      <li><code>manual</code> — created from the A/B Tests tab "New test" button with no prior context.</li>
      <li><code>upload</code> — backfilled via the Upload Past Test flow.</li>
    </ul>

    <Sub>End-to-end flow</Sub>
    <FlowSteps
      items={[
        { actor: "user", label: "Trigger a test from Scorecard, Brand Signal, Keyword, Landscape, SEO Insight, or the A/B Tests tab" },
        { actor: "signal", label: "Create flow opens pre-filled with hypothesis, hero ASIN, and source attribution" },
        { actor: "user", label: "Refine variants (intent and/or keyword), generate Variant B content with AI; Save Test parks it in Planning, Create Test launches it as Running" },
        { actor: "user", label: "Set up the experiment in Amazon Manage Your Experiments; track progress in ClickUp" },
        { actor: "user", label: "When the test hits significance (or the team agrees to end), download the results CSV from Amazon" },
        { actor: "user", label: "Upload results in Signal — status flips to Complete, metrics populate the row" },
        { actor: "signal", label: "Signal reads the winning variant's hypothesis matrix and contributes votes to every matching Brand Signal" },
      ]}
    />

    <Sub>Two upload paths</Sub>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li>
        <strong className="text-foreground">Upload Results</strong> — Used on a Running test.
        Captures Amazon metrics (CVR, sales, sessions), an optional Variant B switch date,
        and the outcome (winner, no significant difference, etc.). Sets <code>status: "completed"</code> and writes <code>amazonMetrics</code>.
      </li>
      <li>
        <strong className="text-foreground">Upload Past Test</strong> — Used to log a test
        that was never created in Signal. Drag-and-drop CSV or screenshot; the parser
        extracts what it can and asks for missing metadata. Lands as a completed test row
        with <code>source: "upload"</code>.
      </li>
    </ul>

    <P>
      <strong>State machine.</strong> Tests move through three primary states:
      {" "}<Pill>Planning</Pill> → <Pill>Running</Pill> → <Pill>Complete</Pill>, with{" "}
      <Pill variant="muted">Archived</Pill> as a side-channel for tests excluded from active
      dashboards. <Pill>Save Test</Pill> in the editor lands a test in Planning;{" "}
      <Pill>Create Test</Pill> lands it directly in Running. Clicking a Planning row reopens
      the create modal with state restored so the user can keep editing or promote it to
      Running.
    </P>

    <P>
      See the <RefLink to="brand-signals">Brand Signals section</RefLink> below for the full
      evidence model, maturity tiers, and how the signal pool aggregates votes from many
      tests into a single stable hypothesis.
    </P>

    <Sub id="ds-search-terms">6. Search Terms — Generation Logic</Sub>
    <P>
      Search terms can only be generated when at least one landscape is available for the product.
      If no landscapes exist, the user is prompted to generate one first.
    </P>
    <P>
      The generation process uses the highest-KPS keywords from all associated keyword landscapes
      and applies a multi-step refinement:
    </P>
    <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li><strong className="text-foreground">Strip listing references</strong> — Remove any keywords already present in the title, brand, or bullet points.</li>
      <li><strong className="text-foreground">Remove competitor brands</strong> — Exclude competitor brand names and restricted terms.</li>
      <li><strong className="text-foreground">Eliminate filler</strong> — Remove conjunctions and prepositions.</li>
      <li><strong className="text-foreground">Deduplicate</strong> — Remove duplicate keywords from the final set.</li>
    </ol>
    <P>
      The final output prioritizes unique, high-value individual keywords to maximize the
      effectiveness of backend search terms within Amazon's character limits.
    </P>
  </>
);
